import { Platform } from '@angular/cdk/platform';
import { Inject, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Angulartics2GoogleTagManager, Angulartics2Matomo, TrackEventMatomoProperties } from 'angulartics2';
import Env from '../../env';

export enum VendorType {
    GOOGLE_GTM = 'google-gtm',
    MATOMO = 'matomo'
}

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private vendor: Env['ANALYTICS'];
    private defaultVendor: Env['ANALYTICS'] = {
        TYPE: 'google-gtm',
        ID: 'GTM-MFT2LM8'
    };
    private isLoaded = false;

    constructor(
        private router: Router,
        @Inject('ENV') public env: Env,
        private platform: Platform,
        private gtmAnalytics: Angulartics2GoogleTagManager,
        private matomoAnalytics: Angulartics2Matomo,
        private zone: NgZone,
        private titleService: Title
    ) {}

    public async initializeAnalytics(): Promise<void> {
        if (this.env.ENV !== 'local') {
            this.vendor = this.env.ANALYTICS || this.defaultVendor;
            switch (this.vendor?.TYPE) {
                case VendorType.GOOGLE_GTM:
                    await this.loadGoogleTag();
                    this.gtmAnalytics.startTracking();
                    break;
                case VendorType.MATOMO:
                    await this.loadMatomo();
                    this.matomoAnalytics.startTracking();
                    break;
                default:
            }
        }
    }

    private loadGoogleTag(): Promise<boolean> {
        this.onRouteChange();
        return new Promise((resolve, reject) => {
            if (this.isLoaded) {
                resolve(true);
                return;
            }

            const analyticsId = this.vendor?.ID;

            if (!analyticsId) {
                reject(new Error('Google Analytics ID is missing'));
                return;
            }

            const gaConfigScript = document.createElement('script');

            gaConfigScript.id = 'google-tag-config';
            gaConfigScript.textContent = `
            window.dataLayer = window.dataLayer || [];

            function gtag() {
                dataLayer.push(arguments);
            }

            gtag('js', new Date());

            gtag('config', '${analyticsId}', {
                cookie_flags: 'SameSite=None;Secure'
            });
        `;

            const gaScript = document.createElement('script');

            gaScript.id = 'google-tag-script';
            gaScript.async = true;
            gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;

            gaScript.addEventListener('load', () => {
                this.isLoaded = true;
                resolve(true);
            });

            gaScript.addEventListener('error', () => {
                reject(new Error('Failed to load Google tag'));
            });

            document.head.appendChild(gaConfigScript);
            document.head.appendChild(gaScript);
        });
    }

    public loadMatomo(): Promise<boolean> {
        this.onRouteChange();

        return new Promise((resolve) => {
            if (this.isLoaded) {
                return resolve(this.isLoaded);
            }

            this.zone.runOutsideAngular(() => {
                const matomoScript = document.createElement('script');
                const url = `https://${this.vendor.URL}/`;
                let containerId: string;

                if (this.platform.isBrowser) {
                    containerId = this.vendor.CHANNELS.WEB;
                }
                if (this.platform.IOS) {
                    containerId = this.vendor.CHANNELS.IOS;
                }
                if (this.platform.ANDROID) {
                    containerId = this.vendor.CHANNELS.ANDROID;
                }

                matomoScript.innerHTML = `
                var _paq = window._paq = window._paq || [];

                _paq.push(['enableLinkTracking']);
                (function() {
                    var u = '${url}';
                    _paq.push(['setTrackerUrl', u+'matomo.php']);
                    _paq.push(['setSiteId', '${containerId}']);
                    var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
                    g.type='text/javascript'; g.async=true; g.defer=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
                })();
                `;

                document.head.insertBefore(matomoScript, document.head.firstChild);
            });

            this.isLoaded = true;
        });
    }

    public eventTrack(action: string, properties: any | TrackEventMatomoProperties) {
        if (!this.vendor) {
            return;
        }

        if (this.vendor.TYPE === 'google-gtm') {
            this.gtmAnalytics.eventTrack(action, properties);
        }
        if (this.vendor.TYPE === 'matomo') {
            this.matomoAnalytics.eventTrack(action, properties);
        }
    }

    private onRouteChange() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                if (this.vendor.TYPE === 'google-gtm') {
                    this.gtmAnalytics.pageTrack(event.urlAfterRedirects);
                }
                if (this.vendor.TYPE === 'matomo') {
                    this.zone.runOutsideAngular(() => {
                        const trackUrl = event.urlAfterRedirects;

                        try {
                            if (window['_paq']) {
                                window['_paq'].push(['setDocumentTitle', this.titleService.getTitle()]);
                                window['_paq'].push(['setCustomUrl', `https://${this.env.APP_NAME + trackUrl}`]);
                                window['_paq'].push(['trackPageView']);
                            }
                        } catch (e) {
                            console.error('Error tracking page view in Matomo:', e);
                        }
                    });
                }
            }
        });
    }
}
